import * as React from "react";
import { cn } from "@/lib/utils";

/* =======================================================
   LOWKEYX // INTELLIGENCE CONSOLE v2 — Primitives
   ======================================================= */

/* ---------- SectionEyebrow ---------- */
export const SectionEyebrow: React.FC<{
  index?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ index, children, className }) => (
  <div
    className={cn(
      "flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/80",
      className
    )}
  >
    {index && <span className="text-primary">{index}</span>}
    <span className="block w-6 h-px bg-primary/60" />
    <span>{children}</span>
  </div>
);

/* ---------- SectionTitle ---------- */
export const SectionTitle: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <h2
    className={cn(
      "intel-display text-[clamp(1.75rem,4vw,3.25rem)] text-foreground mt-3",
      className
    )}
  >
    {children}
  </h2>
);

/* ---------- IntelCard ---------- */
export const IntelCard: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    moduleId?: string;
    status?: "live" | "ok" | "warn" | "err" | "off";
    meta?: React.ReactNode;
    children: React.ReactNode;
  }
> = ({ moduleId, status = "off", meta, children, className, ...rest }) => {
  const dot = {
    live: "bg-primary intel-pulse-red",
    ok: "bg-signal-green",
    warn: "bg-signal-amber",
    err: "bg-primary",
    off: "bg-muted-foreground/40",
  }[status];
  return (
    <div
      {...rest}
      className={cn(
        "relative bg-surface-2 border border-border hover:border-border-strong/60 transition-colors group",
        className
      )}
    >
      {(moduleId || meta) && (
        <div className="flex items-center justify-between px-4 h-8 bg-surface-1/60 border-b border-border font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
          <span className="flex items-center gap-2 truncate">
            <span className={cn("inline-block w-1.5 h-1.5 rounded-full", dot)} />
            <span className="truncate">{moduleId}</span>
          </span>
          {meta && <span className="text-foreground/70 shrink-0">{meta}</span>}
        </div>
      )}
      <div className="p-5">{children}</div>
      {/* hover bottom rail */}
      <span className="pointer-events-none absolute left-0 right-0 bottom-0 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
    </div>
  );
};

/* ---------- MetricCell ---------- */
export const MetricCell: React.FC<{
  label: string;
  value: string | number;
  unit?: string;
  hint?: string;
  className?: string;
}> = ({ label, value, unit, hint, className }) => (
  <div
    className={cn(
      "bg-surface-2 border border-border px-4 py-4 hover:border-border-strong/60 transition-colors",
      className
    )}
  >
    <div className="font-display text-[1.6rem] leading-none text-foreground tracking-wide tabular-nums">
      {value}
      {unit && <span className="text-primary text-base ml-0.5">{unit}</span>}
    </div>
    <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    {hint && (
      <div className="mt-0.5 font-mono text-[9px] text-muted-foreground/60">
        {hint}
      </div>
    )}
  </div>
);

/* ---------- SeverityBadge ---------- */
const SEV = {
  critical: "bg-primary/15 text-signal-red border-primary/35",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/25",
  medium: "bg-signal-amber/10 text-signal-amber border-signal-amber/25",
  low: "bg-signal-cyan/10 text-signal-cyan border-signal-cyan/25",
  info: "bg-signal-cyan/10 text-signal-cyan border-signal-cyan/25",
  free: "bg-signal-green/10 text-signal-green border-signal-green/25",
  auth: "bg-signal-amber/10 text-signal-amber border-signal-amber/25",
} as const;

export const SeverityBadge: React.FC<{
  level: keyof typeof SEV;
  children: React.ReactNode;
  className?: string;
}> = ({ level, children, className }) => (
  <span
    className={cn(
      "inline-flex items-center font-mono text-[9px] uppercase tracking-[0.1em] px-2 py-0.5 border",
      SEV[level],
      className
    )}
  >
    {children}
  </span>
);

/* ---------- LiveTicker ---------- */
export type TickerItem = { tone?: "ok" | "warn" | "err" | "info"; text: string };
export const LiveTicker: React.FC<{ items: TickerItem[]; className?: string }> = ({
  items,
  className,
}) => {
  const tones: Record<NonNullable<TickerItem["tone"]>, string> = {
    ok: "text-signal-green",
    warn: "text-signal-amber",
    err: "text-signal-red",
    info: "text-signal-cyan",
  };
  const doubled = [...items, ...items];
  return (
    <div
      className={cn(
        "relative overflow-hidden border-y border-border bg-surface-1/60 backdrop-blur-sm",
        className
      )}
    >
      <div className="intel-ticker-track flex gap-12 py-2 whitespace-nowrap font-mono text-[10px] tracking-[0.12em] text-muted-foreground">
        {doubled.map((it, i) => (
          <span key={i} className="flex items-center gap-2 shrink-0">
            <span className={cn("inline-block w-1 h-1 rounded-full bg-current", tones[it.tone || "info"])} />
            <span className={cn("uppercase", tones[it.tone || "info"])}>{it.text}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- StatusRail ---------- */
export const StatusRail: React.FC<{
  left?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}> = ({ left, right, className }) => (
  <div
    className={cn(
      "border-b border-border bg-surface-0/80 backdrop-blur-sm",
      className
    )}
  >
    <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-1.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
      <span className="flex items-center gap-3 truncate">{left}</span>
      <span className="flex items-center gap-3 shrink-0">{right}</span>
    </div>
  </div>
);

/* ---------- TerminalChrome ---------- */
export const TerminalChrome: React.FC<{
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}> = ({ title, children, footer, className }) => (
  <div className={cn("border border-border bg-surface-0 font-mono overflow-hidden", className)}>
    <div className="flex items-center gap-3 px-4 h-9 bg-surface-2 border-b border-border">
      <div className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
      </div>
      <div className="flex-1 text-center text-[10px] tracking-[0.16em] uppercase text-muted-foreground/80">
        {title}
      </div>
      <div className="w-10" />
    </div>
    <div>{children}</div>
    {footer && <div className="border-t border-border bg-surface-2 px-4 py-2.5">{footer}</div>}
  </div>
);

/* ---------- Heatmap ---------- */
export const Heatmap: React.FC<{
  cols?: number;
  rows?: number;
  data?: number[]; // 0..5 intensities
  className?: string;
}> = ({ cols = 14, rows = 7, data, className }) => {
  const total = cols * rows;
  // deterministic pseudo-random fallback
  const cells =
    data && data.length
      ? data.slice(0, total)
      : Array.from({ length: total }, (_, i) => {
          const x = Math.sin(i * 12.9898) * 43758.5453;
          const v = Math.abs(x - Math.floor(x));
          return Math.floor(v * 6);
        });
  const tones = [
    "bg-surface-3",
    "bg-primary/15",
    "bg-primary/30",
    "bg-primary/50",
    "bg-primary/75",
    "bg-primary",
  ];
  return (
    <div className={cn("grid gap-[3px]", className)} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
      {cells.map((v, i) => (
        <div
          key={i}
          className={cn("aspect-square transition-transform hover:scale-[1.4] hover:z-10", tones[Math.max(0, Math.min(5, v))])}
        />
      ))}
    </div>
  );
};

/* ---------- IntelButton (sweep gloss) ---------- */
export const IntelButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "ghost" | "outline";
  }
> = ({ variant = "primary", className, children, ...rest }) => {
  const styles = {
    primary:
      "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-glow))] intel-sweep",
    ghost:
      "bg-transparent text-muted-foreground border border-border hover:text-foreground hover:border-border-strong/60",
    outline:
      "bg-transparent text-foreground border border-border hover:border-primary/60 hover:text-primary",
  }[variant];
  return (
    <button
      {...rest}
      className={cn(
        "inline-flex items-center justify-center gap-2 h-10 px-5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors",
        styles,
        className
      )}
    >
      {children}
    </button>
  );
};
